import { useState, useEffect, useRef } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    RadioGroup,
    FormControlLabel,
    FormControl,
    FormLabel,
    Checkbox,
    Grid,
    IconButton,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Select,
    MenuItem,
    InputLabel,
    Autocomplete,
    CircularProgress,
    ToggleButtonGroup,
    ToggleButton,
    Radio,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@mui/material';
import {
    Add as AddIcon,
    Remove as RemoveIcon,
    Close as CloseIcon,
    Repeat as RepeatIcon,
    Send as SendIcon,
    Edit as EditIcon,
    ExpandMore as ExpandMoreIcon,
    CreditCard as CreditCardIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useAlert } from '../contexts/AlertContext';
import { useSettings } from '../contexts/SettingsContext';
import signalRService from '../services/signalRService';
import { safeInvoke } from '../utils/signalRHelper';
import { driversAPI, ridesAPI, carTypesAPI } from '../services/apiService';
import AddressAutocomplete from './AddressAutocomplete';
import PaymentForm from './PaymentForm';
import { calculateRoute } from '../services/routeCalculator';
import { useGoogleMaps } from './GoogleMapsProvider';
import soundService from '../services/soundService';
import { formatPhoneNumber, PRICING_DEFAULTS, DAYS_OF_WEEK } from '../config/appConstants';

const NewRecurringCallWizard = ({ onComplete, onCancel }) => {
    const { user, signalRConnection, connectSignalR } = useAuth();
    const { showAlert, showToast } = useAlert();
    const { settings, addOns } = useSettings();
    const { isLoaded: googleMapsLoaded } = useGoogleMaps();
    const [drivers, setDrivers] = useState([]);
    const [carTypes, setCarTypes] = useState([]);
    const [formData, setFormData] = useState({
        // Ride object fields
        customerName: '',
        customerPhoneNumber: '',
        cost: 0,
        driversCompensation: 0,
        notes: '',
        paymentType: null, // Driver will select payment type at dropoff (unless CC prepaid)
        passengers: 1,
        carType: '',
        selectedAddOnIds: [],
        assignedToId: null,
        assignedToName: '',
        flightNumber: '',

        // Route object fields
        pickup: '',
        dropOff: '',
        additionalStops: [],
        roundTrip: false,
        estimatedDuration: 0,

        // Payment token (Square tokenization)
        paymentTokenId: null
    });

    // Recurring-specific state
    const [dayConfigs, setDayConfigs] = useState(
        DAYS_OF_WEEK.map(day => ({
            dayOfWeek: day.value,
            enabled: false,
            time: '',
            endDate: ''
        }))
    );

    const [errors, setErrors] = useState({});
    const phoneInputRef = useRef(null);
    const stopRefs = useRef([]);
    const squarePaymentRef = useRef(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isCalculatingRoute, setIsCalculatingRoute] = useState(false);
    const [isCalculatingPrice, setIsCalculatingPrice] = useState(false);
    const [routeInfo, setRouteInfo] = useState({ distance: '', duration: '' });
    const [pricingDetails, setPricingDetails] = useState(null);
    const [isEditingPrice, setIsEditingPrice] = useState(false);
    // Track if user has manually edited the price
    const [priceManuallyEdited, setPriceManuallyEdited] = useState(false);
    // Track current rush hour surcharge applied
    const [currentRushHourSurcharge, setCurrentRushHourSurcharge] = useState(0);
    const [addressesValidated, setAddressesValidated] = useState({ pickup: false, dropOff: false });
    const [stopsValidated, setStopsValidated] = useState([]);
    // Track if CC accordion is expanded (user is entering card info)
    const [ccAccordionExpanded, setCcAccordionExpanded] = useState(false);

    // Fetch all drivers and car types on component mount
    useEffect(() => {
        const fetchDrivers = async () => {
            try {
                const data = await driversAPI.getAll();
                setDrivers(data || []);
            } catch (error) {
                console.error('Error fetching drivers:', error);
            }
        };
        const fetchCarTypes = async () => {
            try {
                const data = await carTypesAPI.getAll();
                const sorted = (data || []).sort((a, b) => a.sortOrder - b.sortOrder);
                setCarTypes(sorted);
                if (sorted.length > 0) {
                    setFormData(prev => ({ ...prev, carType: prev.carType || sorted[0].id }));
                }
            } catch (error) {
                console.error('Error fetching car types:', error);
            }
        };
        fetchDrivers();
        fetchCarTypes();
    }, []);

    // (formatPhoneNumber imported from appConstants)

    const extractPhoneDigits = (value) => {
        return value.replace(/\D/g, '');
    };

    const handlePhoneBackspace = (e, fieldName) => {
        if (e.key === 'Backspace') {
            const input = e.target;
            const cursorPos = input.selectionStart;
            const value = input.value;
            const charBefore = value[cursorPos - 1];

            if (cursorPos > 0 && (charBefore === '(' || charBefore === ')' || charBefore === ' ' || charBefore === '-')) {
                e.preventDefault();
                let removeCount;
                if (charBefore === ' ') {
                    removeCount = Math.min(cursorPos, 3);
                } else if (charBefore === '(') {
                    removeCount = cursorPos >= 2 ? 2 : 1;
                } else {
                    removeCount = cursorPos >= 2 ? 2 : 1;
                }
                const newValue = value.slice(0, cursorPos - removeCount) + value.slice(cursorPos);
                handleInputChange(fieldName, newValue);
            }
        }
    };

    const handleInputChange = (field, value) => {
        let processedValue = value;

        if (field === 'customerPhoneNumber') {
            const input = phoneInputRef.current;
            const cursorPos = input?.selectionStart || 0;
            const oldValue = formData.customerPhoneNumber || '';
            const oldDigits = extractPhoneDigits(oldValue);
            const newDigits = extractPhoneDigits(value);
            processedValue = formatPhoneNumber(newDigits);

            if (newDigits.length < oldDigits.length) {
                setTimeout(() => {
                    if (input) {
                        let newPos = cursorPos;
                        if (processedValue[cursorPos - 1] === ')' ||
                            processedValue[cursorPos - 1] === ' ' ||
                            processedValue[cursorPos - 1] === '-') {
                            newPos = cursorPos - 1;
                        }
                        input.setSelectionRange(newPos, newPos);
                    }
                }, 0);
            }
        }

        setFormData(prev => ({
            ...prev,
            [field]: processedValue
        }));

        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }

        if (field === 'dropOff' || field === 'pickup') {
            setAddressesValidated(prev => ({
                ...prev,
                [field]: false
            }));
        }
    };

    // Calculate route when addresses change
    const calculateTripRoute = async () => {
        if (!googleMapsLoaded || !formData.pickup || !formData.dropOff) {
            setRouteInfo({ distance: '', duration: '' });
            setFormData(prev => ({ ...prev, estimatedDuration: 0, cost: 0, driversCompensation: 0 }));
            setPricingDetails(null);
            return;
        }

        if (!addressesValidated.pickup || !addressesValidated.dropOff) {
            return;
        }

        setIsCalculatingRoute(true);
        setIsCalculatingPrice(true);
        try {
            const validatedStops = formData.additionalStops.filter((s, index) =>
                s && s.trim() !== '' && stopsValidated.includes(index)
            );

            const result = await calculateRoute(
                formData.pickup,
                formData.dropOff,
                validatedStops,
                formData.roundTrip
            );

            if (result.error) {
                setRouteInfo({ distance: '', duration: '' });
            } else {
                setRouteInfo({
                    distance: result.distanceText,
                    duration: result.durationText
                });
                setFormData(prev => ({
                    ...prev,
                    estimatedDuration: result.duration
                }));
            }

            await calculatePricing(validatedStops, true);
            // Reset manual edit flag since route changed
            setPriceManuallyEdited(false);
        } catch (error) {
            setRouteInfo({ distance: '', duration: '' });
        } finally {
            setIsCalculatingRoute(false);
        }
    };

    // Calculate pricing
    const calculatePricing = async (validatedStops = [], forceRecalc = false) => {
        try {
            // Use the first enabled day's time for pricing calculation, or empty if none
            const firstEnabledDay = dayConfigs.find(d => d.enabled && d.time);
            let scheduledDateTime = null;
            if (firstEnabledDay) {
                const today = new Date();
                scheduledDateTime = new Date(`${today.toISOString().split('T')[0]}T${firstEnabledDay.time}`).toISOString();
            }

            const priceRequest = {
                pickup: formData.pickup,
                dropOff: formData.dropOff,
                stops: validatedStops,
                carType: formData.carType || 1,
                isRoundTrip: formData.roundTrip,
                scheduledTime: scheduledDateTime
            };

            const response = await ridesAPI.calculatePrice(priceRequest);

            if (response.success) {
                const newRushHourSurcharge = response.rushHourSurcharge || 0;

                // If price was manually edited and this isn't a forced recalc,
                // only adjust the rush hour difference
                if (priceManuallyEdited && !forceRecalc) {
                    const rushHourDiff = newRushHourSurcharge - currentRushHourSurcharge;
                    if (rushHourDiff !== 0) {
                        setFormData(prev => ({
                            ...prev,
                            cost: Math.round(prev.cost + rushHourDiff),
                            driversCompensation: Math.round(prev.driversCompensation + (rushHourDiff * PRICING_DEFAULTS.rushHourDriverCompRatio))
                        }));
                        setCurrentRushHourSurcharge(newRushHourSurcharge);
                    }
                    setPricingDetails(prev => ({ ...prev, rushHourSurcharge: newRushHourSurcharge }));
                } else {
                    // Full price update
                    setPricingDetails(response);
                    setCurrentRushHourSurcharge(newRushHourSurcharge);
                    const addOnsTotal = (formData.selectedAddOnIds || []).reduce(
                        (sum, id) => sum + (addOns.find(a => a.id === id)?.fee || 0), 0
                    );
                    setFormData(prev => ({
                        ...prev,
                        // Add-on fees go entirely to the driver, so add them to both cost and driver compensation
                        cost: Math.round(response.totalPrice) + addOnsTotal,
                        driversCompensation: Math.round(response.driversCompensation) + addOnsTotal
                    }));
                }
            } else {
                if (!priceManuallyEdited) {
                    setPricingDetails(null);
                    setFormData(prev => ({
                        ...prev,
                        cost: 0,
                        driversCompensation: 0
                    }));
                }
            }
        } catch (error) {
            if (!priceManuallyEdited) {
                setPricingDetails(null);
                setFormData(prev => ({
                    ...prev,
                    cost: 0,
                    driversCompensation: 0
                }));
            }
        } finally {
            setIsCalculatingPrice(false);
        }
    };

    // Trigger route calculation when route-related fields change (full recalculation)
    useEffect(() => {
        const timer = setTimeout(() => {
            if (formData.pickup && formData.dropOff && addressesValidated.pickup && addressesValidated.dropOff) {
                calculateTripRoute();
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [formData.pickup, formData.dropOff, formData.additionalStops, formData.roundTrip, formData.carType, formData.selectedAddOnIds, googleMapsLoaded, addressesValidated, stopsValidated]);

    // Separate effect for schedule changes - only adjust rush hour surcharge
    useEffect(() => {
        if (!formData.pickup || !formData.dropOff || !addressesValidated.pickup || !addressesValidated.dropOff) {
            return;
        }
        if (formData.cost === 0) {
            return; // No initial price calculated yet
        }

        const timer = setTimeout(async () => {
            setIsCalculatingPrice(true);
            const validatedStops = formData.additionalStops.filter((s, index) =>
                s && s.trim() !== '' && stopsValidated.includes(index)
            );
            await calculatePricing(validatedStops, false);
        }, 500);

        return () => clearTimeout(timer);
    }, [dayConfigs]);

    const handleAddressSelect = (field, address, placeDetails) => {
        setFormData(prev => ({
            ...prev,
            [field]: address
        }));
        setAddressesValidated(prev => ({
            ...prev,
            [field]: true
        }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    const handleStopSelect = (index, address, placeDetails) => {
        const newStops = [...formData.additionalStops];
        newStops[index] = address;
        setFormData(prev => ({
            ...prev,
            additionalStops: newStops
        }));
        setStopsValidated(prev => {
            if (!prev.includes(index)) {
                return [...prev, index];
            }
            return prev;
        });
    };

    const addStop = () => {
        if (formData.additionalStops.length >= 10) {
            showAlert('Maximum Stops', 'Maximum 10 stops allowed', [{ text: 'OK' }], 'warning');
            return;
        }
        const newStops = [...formData.additionalStops, ''];
        setFormData(prev => ({
            ...prev,
            additionalStops: newStops
        }));
        setTimeout(() => {
            const lastIndex = newStops.length - 1;
            if (stopRefs.current[lastIndex]) {
                stopRefs.current[lastIndex].focus();
            }
        }, 100);
    };

    const removeStop = (index) => {
        const newStops = formData.additionalStops.filter((_, i) => i !== index);
        setFormData(prev => ({
            ...prev,
            additionalStops: newStops
        }));
        setStopsValidated(prev =>
            prev
                .filter(i => i !== index)
                .map(i => i > index ? i - 1 : i)
        );
    };

    const updateStop = (index, value) => {
        const newStops = [...formData.additionalStops];
        newStops[index] = value;
        setFormData(prev => ({
            ...prev,
            additionalStops: newStops
        }));
        setStopsValidated(prev => prev.filter(i => i !== index));
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.customerPhoneNumber.trim()) {
            newErrors.customerPhoneNumber = 'Phone number is required';
        } else {
            const phoneDigits = formData.customerPhoneNumber.replace(/\D/g, '');
            if (phoneDigits.length !== 10) {
                newErrors.customerPhoneNumber = 'Phone number must be 10 digits';
            }
        }

        if (!formData.pickup.trim()) {
            newErrors.pickup = 'Pickup location is required';
        }

        if (!formData.dropOff.trim()) {
            newErrors.dropOff = 'Drop-off location is required';
        }

        // Passengers is optional - will default to 1 if blank when sending to server

        // Validate recurring-specific fields
        const enabledDays = dayConfigs.filter(d => d.enabled);
        if (enabledDays.length === 0) {
            newErrors.dayConfigs = 'Select at least one day';
        } else {
            // Validate each enabled day has time and end date
            enabledDays.forEach(day => {
                if (!day.time) {
                    newErrors[`day_${day.dayOfWeek}_time`] = 'Time required';
                }
                if (!day.endDate) {
                    newErrors[`day_${day.dayOfWeek}_endDate`] = 'End date required';
                } else {
                    const selectedEndDate = new Date(day.endDate);
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    if (selectedEndDate < today) {
                        newErrors[`day_${day.dayOfWeek}_endDate`] = 'Must be in future';
                    }
                }
            });
        }

        // No payment validation needed - CC is optional, driver handles payment at dropoff

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Get the next occurrence of a given day of week from today
    const getNextOccurrence = (dayOfWeek, time) => {
        const now = new Date();
        const [hours, minutes] = time.split(':').map(Number);

        let result = new Date(now);
        result.setHours(hours, minutes, 0, 0);

        // Calculate days until next occurrence
        const currentDay = now.getDay();
        let daysUntilNext = dayOfWeek - currentDay;

        // If the day is today but time has passed, or day is in the past, go to next week
        if (daysUntilNext < 0 || (daysUntilNext === 0 && result <= now)) {
            daysUntilNext += 7;
        }

        result.setDate(result.getDate() + daysUntilNext);
        return result;
    };

    const handlePaymentTokenGenerated = (token, cardholderName) => {
        setFormData(prev => ({
            ...prev,
            paymentTokenId: token
        }));
        showToast(`Card tokenized successfully for ${cardholderName}`, 'success');
    };

    const handleTokenError = (error) => {
        console.error('❌ Tokenization error:', error);
        showAlert('Error', `Failed to process card: ${error}`, [{ text: 'OK' }]);
    };

    const handleSubmit = async () => {
        // Validate form (CC is optional, no special tokenization logic needed)
        if (!validateForm()) return;

        // If CC accordion is expanded but card not tokenized, show error
        if (ccAccordionExpanded && !formData.paymentTokenId) {
            showAlert(
                'Card Not Saved',
                'You have the credit card form open but haven\'t saved the card yet.\n\nPlease either:\n• Click "Save Card" to save the card\n• Or collapse the credit card section if you don\'t want to add a card',
                [{ text: 'OK' }],
                'warning'
            );
            return;
        }

        setIsSubmitting(true);
        try {

            // Create one ride for each enabled day
            const ridesCreated = [];
            const enabledDays = dayConfigs.filter(d => d.enabled);

            for (const dayConfig of enabledDays) {
                const scheduledFor = getNextOccurrence(dayConfig.dayOfWeek, dayConfig.time);
                const now = new Date();

                // Convert estimatedDuration to TimeOnly format
                const durationMinutes = formData.estimatedDuration || 0;
                const hours = Math.floor(durationMinutes / 60);
                const minutes = Math.floor(durationMinutes % 60);
                const estimatedDurationTime = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`;

                // Determine payment type and paid status based on whether CC was tokenized
                const hasPaymentToken = !!formData.paymentTokenId;
                const paymentType = hasPaymentToken ? 'dispatcherCC' : null; // null = driver will select at dropoff
                const isPaid = hasPaymentToken; // Prepaid if CC was tokenized

                // Build ride object
                const rideObject = {
                    rideId: 0,
                    routeId: 0,
                    customerName: formData.customerName,
                    customerPhoneNumber: formData.customerPhoneNumber.replace(/\D/g, ''),
                    callTime: now.toISOString(),
                    scheduledFor: scheduledFor.toISOString(),
                    pickupTime: null,
                    dropOffTime: null,
                    cost: parseInt(formData.cost) || 0,
                    driversCompensation: parseInt(formData.driversCompensation) || 0,
                    paidTime: null,
                    assignedToId: formData.assignedToId || null,
                    reassignedToId: null,
                    notes: formData.notes || "",
                    dispatchedById: user?.userId || null,
                    paymentType: paymentType,
                    paymentTokenId: formData.paymentTokenId || null,
                    paid: isPaid,
                    reassigned: false,
                    canceled: false,
                    passengers: parseInt(formData.passengers) || 1,
                    selectedAddOnIds: (formData.selectedAddOnIds && formData.selectedAddOnIds.length > 0)
                        ? JSON.stringify(formData.selectedAddOnIds)
                        : null,
                    carType: formData.carType || 1,
                    isReoccurring: true,
                    flightNumber: formData.flightNumber || null,
                    stopsCost: pricingDetails?.stopsCost ?? null,
                    stopsDriverComp: pricingDetails?.stopsDriverComp ?? null,
                    route: {
                        routeId: 0,
                        pickup: formData.pickup,
                        dropOff: formData.dropOff,
                        stop1: formData.additionalStops[0] || null,
                        stop2: formData.additionalStops[1] || null,
                        stop3: formData.additionalStops[2] || null,
                        stop4: formData.additionalStops[3] || null,
                        stop5: formData.additionalStops[4] || null,
                        stop6: formData.additionalStops[5] || null,
                        stop7: formData.additionalStops[6] || null,
                        stop8: formData.additionalStops[7] || null,
                        stop9: formData.additionalStops[8] || null,
                        stop10: formData.additionalStops[9] || null,
                        roundTrip: formData.roundTrip,
                        callTime: now.toISOString(),
                        estimatedDuration: estimatedDurationTime,
                    }
                };

                // Build recurring object with unique time and end date
                const recurringObject = {
                    id: 0,
                    dayOfWeek: dayConfig.dayOfWeek,
                    time: `${dayConfig.time}:00`, // Add seconds for TimeOnly format (HH:mm:ss)
                    endDate: new Date(dayConfig.endDate).toISOString()
                };

                // Stamp a one-time idempotency key — no retries on non-idempotent create
                rideObject.clientRequestId = crypto.randomUUID();

                // NewCallCreated is NOT idempotent — never retry
                const result = await safeInvoke({
                    connection: signalRConnection,
                    connectFn: connectSignalR,
                    methodName: 'NewCallCreated',
                    args: [rideObject, recurringObject],
                    maxRetries: 0,
                });

                if (result.success) {
                    ridesCreated.push({ ride: rideObject, day: DAYS_OF_WEEK.find(d => d.value === dayConfig.dayOfWeek)?.label });
                } else {
                    console.error('Failed to create recurring call:', result.error);
                    const errMsg = result.error?.message || result.error || 'Unknown error';
                    const dayLabel = DAYS_OF_WEEK.find(d => d.value === dayConfig.dayOfWeek)?.label;
                    const createdStr = ridesCreated.length
                        ? `\n\nAlready created: ${ridesCreated.map(r => r.day).join(', ')}`
                        : '';
                    showAlert(
                        'Failed to Create Recurring Call',
                        `Failed to create the call for ${dayLabel}.\n\nDetails: ${errMsg}${createdStr}`,
                        [{ text: 'OK' }],
                        'error'
                    );
                    return;
                }
            }

            soundService.playCallSentSound();

            const daysStr = ridesCreated.map(r => r.day).join(', ');
            showToast(`Recurring call created for: ${daysStr}`, 'success');

            clearForm();
            onComplete(ridesCreated);
        } catch (error) {
            console.error('Failed to create recurring ride:', error);
            showAlert('Error', 'Failed to create recurring ride. Please try again.', [{ text: 'OK' }], 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const clearForm = () => {
        setFormData({
            customerName: '',
            customerPhoneNumber: '',
            cost: 0,
            driversCompensation: 0,
            notes: '',
            paymentType: null, // Driver will select payment type at dropoff (unless CC prepaid)
            passengers: 1,
            carType: '',
            selectedAddOnIds: [],
            assignedToId: null,
            assignedToName: '',
            flightNumber: '',
            pickup: '',
            dropOff: '',
            additionalStops: [],
            roundTrip: false,
            estimatedDuration: 0,
            paymentTokenId: null
        });
        setDayConfigs(
            DAYS_OF_WEEK.map(day => ({
                dayOfWeek: day.value,
                enabled: false,
                time: '',
                endDate: ''
            }))
        );
        setErrors({});
        setRouteInfo({ distance: '', duration: '' });
        setAddressesValidated({ pickup: false, dropOff: false });
        setStopsValidated([]);
        setPricingDetails(null);
        setIsEditingPrice(false);
        setCcAccordionExpanded(false);
        // Reset Square payment form
        if (squarePaymentRef.current) {
            squarePaymentRef.current.reset();
        }
    };

    const getTodayDate = () => {
        const today = new Date();
        return today.toISOString().split('T')[0];
    };

    const handleDayToggle = (dayOfWeek) => {
        setDayConfigs(prev => prev.map(day =>
            day.dayOfWeek === dayOfWeek
                ? { ...day, enabled: !day.enabled }
                : day
        ));
        if (errors.dayConfigs) {
            setErrors(prev => ({ ...prev, dayConfigs: '' }));
        }
    };

    const handleDayTimeChange = (dayOfWeek, time) => {
        setDayConfigs(prev => prev.map(day =>
            day.dayOfWeek === dayOfWeek
                ? { ...day, time }
                : day
        ));
        if (errors[`day_${dayOfWeek}_time`]) {
            setErrors(prev => ({ ...prev, [`day_${dayOfWeek}_time`]: '' }));
        }
    };

    const handleDayEndDateChange = (dayOfWeek, endDate) => {
        setDayConfigs(prev => prev.map(day =>
            day.dayOfWeek === dayOfWeek
                ? { ...day, endDate }
                : day
        ));
        if (errors[`day_${dayOfWeek}_endDate`]) {
            setErrors(prev => ({ ...prev, [`day_${dayOfWeek}_endDate`]: '' }));
        }
    };

    return (
        <Dialog open={true} onClose={onCancel} maxWidth="lg" fullWidth>
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box display="flex" alignItems="center" gap={1}>
                        <RepeatIcon color="primary" />
                        <Typography variant="h5">New Recurring Call</Typography>
                    </Box>
                    <IconButton onClick={onCancel}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    This will create a recurring ride that repeats on selected days until the end date
                </Typography>
            </DialogTitle>

            <DialogContent>
                <Grid container spacing={3} sx={{ mt: 1 }}>
                    {/* Left Side - Call Details */}
                    <Grid item xs={12} md={7}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Call Details
                                </Typography>

                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Customer Name"
                                            value={formData.customerName}
                                            onChange={(e) => handleInputChange('customerName', e.target.value)}
                                            error={!!errors.customerName}
                                            helperText={errors.customerName}
                                            placeholder="Enter customer name (optional)"
                                            margin="normal"
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Customer Number"
                                            type="tel"
                                            value={formData.customerPhoneNumber}
                                            onChange={(e) => handleInputChange('customerPhoneNumber', e.target.value)}
                                            onKeyDown={(e) => handlePhoneBackspace(e, 'customerPhoneNumber')}
                                            error={!!errors.customerPhoneNumber}
                                            helperText={errors.customerPhoneNumber}
                                            placeholder="(555) 123-4567"
                                            inputRef={phoneInputRef}
                                            inputProps={{ maxLength: 14 }}
                                            margin="normal"
                                            required
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <AddressAutocomplete
                                            label="Pickup Location"
                                            value={formData.pickup}
                                            onChange={(address, placeDetails) => handleAddressSelect('pickup', address, placeDetails)}
                                            onInputChange={(value) => handleInputChange('pickup', value)}
                                            error={!!errors.pickup}
                                            helperText={errors.pickup}
                                            placeholder="Enter pickup address"
                                            required
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <AddressAutocomplete
                                            label="Drop-off Location"
                                            value={formData.dropOff}
                                            onChange={(address, placeDetails) => handleAddressSelect('dropOff', address, placeDetails)}
                                            onInputChange={(value) => handleInputChange('dropOff', value)}
                                            error={!!errors.dropOff}
                                            helperText={errors.dropOff}
                                            placeholder="Enter drop-off address"
                                            required
                                        />
                                    </Grid>


                                    {/* Passengers */}
                                    <Grid item xs={12} sm={3}>
                                        <TextField
                                            fullWidth
                                            label="Passengers"
                                            type="number"
                                            value={formData.passengers}
                                            onChange={(e) => handleInputChange('passengers', e.target.value === '' ? '' : parseInt(e.target.value) || '')}
                                            error={!!errors.passengers}
                                            helperText={errors.passengers || 'Default: 1'}
                                            inputProps={{ min: 1 }}
                                            InputLabelProps={{ shrink: true }}
                                            margin="normal"
                                        />
                                    </Grid>

                                    {/* Car Type */}
                                    <Grid item xs={12} sm={3}>
                                        <FormControl fullWidth margin="normal" required>
                                            <InputLabel shrink>Car Type</InputLabel>
                                            <Select
                                                value={formData.carType}
                                                label="Car Type"
                                                displayEmpty
                                                notched
                                                onChange={(e) => handleInputChange('carType', e.target.value)}
                                            >
                                                {carTypes.map((type) => (
                                                    <MenuItem key={type.id} value={type.id}>
                                                        {type.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>



                                    {/* Assigned To Driver */}
                                    <Grid item xs={12} sm={6}>
                                        <Autocomplete
                                            options={drivers}
                                            getOptionLabel={(option) => option.name || ''}
                                            value={drivers.find(d => d.id === formData.assignedToId) || null}
                                            onChange={(e, newValue) => {
                                                handleInputChange('assignedToId', newValue?.id || null);
                                                handleInputChange('assignedToName', newValue?.name || '');
                                            }}
                                            sx={{ minWidth: 200 }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Assign to Driver"
                                                    placeholder="Search drivers..."
                                                    margin="normal"
                                                    InputLabelProps={{ shrink: true }}
                                                    sx={{ '& .MuiInputBase-root': { minWidth: 180 } }}
                                                />
                                            )}
                                            isOptionEqualToValue={(option, value) => option.id === value?.id}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={3}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', mt: 2.5, gap: -0.5 }}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={formData.roundTrip}
                                                        onChange={(e) => handleInputChange('roundTrip', e.target.checked)}
                                                        size="small"
                                                        sx={{ py: 0.25 }}
                                                    />
                                                }
                                                label={<Typography variant="caption">Round Trip</Typography>}
                                                sx={{ m: 0, height: 24 }}
                                            />
                                            {addOns.map(addOn => (
                                                <FormControlLabel
                                                    key={addOn.id}
                                                    control={
                                                        <Checkbox
                                                            checked={(formData.selectedAddOnIds || []).includes(addOn.id)}
                                                            onChange={(e) => {
                                                                const current = formData.selectedAddOnIds || [];
                                                                const updated = e.target.checked
                                                                    ? [...current, addOn.id]
                                                                    : current.filter(id => id !== addOn.id);
                                                                const diff = e.target.checked ? addOn.fee : -addOn.fee;
                                                                handleInputChange('selectedAddOnIds', updated);
                                                                // Add-on fee goes entirely to the driver
                                                                setFormData(prev => ({ ...prev, selectedAddOnIds: updated, cost: Math.max(0, prev.cost + diff), driversCompensation: Math.max(0, prev.driversCompensation + diff) }));
                                                            }}
                                                            color="primary"
                                                            size="small"
                                                            sx={{ py: 0.25 }}
                                                        />
                                                    }
                                                    label={<Typography variant="caption">{addOn.name} (+${addOn.fee})</Typography>}
                                                    sx={{ m: 0, height: 24 }}
                                                />
                                            ))}
                                        </Box>
                                    </Grid>
                                </Grid>

                                {/* Additional Stops */}
                                <Box sx={{ mt: 2 }}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                        <Typography variant="subtitle2">
                                            Additional Stops {formData.additionalStops.length > 0 && `(${formData.additionalStops.length}/10)`}
                                        </Typography>
                                        <Button
                                            size="small"
                                            startIcon={<AddIcon />}
                                            onClick={addStop}
                                            variant="outlined"
                                            disabled={formData.additionalStops.length >= 10}
                                        >
                                            Add Stop
                                        </Button>
                                    </Box>

                                    {Array.isArray(formData.additionalStops) && formData.additionalStops.map((stop, index) => (
                                        <Box key={index} display="flex" alignItems="flex-start" gap={1} mb={1}>
                                            <Box sx={{ flex: 1 }}>
                                                <AddressAutocomplete
                                                    ref={(el) => (stopRefs.current[index] = el)}
                                                    label={`Stop ${index + 1}`}
                                                    value={stop}
                                                    onChange={(address, placeDetails) => handleStopSelect(index, address, placeDetails)}
                                                    onInputChange={(value) => updateStop(index, value)}
                                                    placeholder={`Stop ${index + 1} address`}
                                                    size="small"
                                                    margin="none"
                                                />
                                            </Box>
                                            <IconButton
                                                size="small"
                                                onClick={() => removeStop(index)}
                                                color="error"
                                                sx={{ mt: 1 }}
                                            >
                                                <RemoveIcon />
                                            </IconButton>
                                        </Box>
                                    ))}
                                </Box>

                                {/* Route Info and Cost Row */}
                                <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                                    {/* Route Info */}
                                    {(routeInfo.distance || routeInfo.duration || isCalculatingRoute) && (
                                        <Box>
                                            <Typography variant="subtitle2" gutterBottom>
                                                Trip Estimate
                                            </Typography>
                                            {isCalculatingRoute ? (
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <CircularProgress size={16} />
                                                    <Typography variant="body2" color="text.secondary">
                                                        Calculating...
                                                    </Typography>
                                                </Box>
                                            ) : (
                                                <Box display="flex" gap={1}>
                                                    {routeInfo.distance && (
                                                        <Chip
                                                            label={routeInfo.distance}
                                                            size="small"
                                                            variant="outlined"
                                                            color="info"
                                                        />
                                                    )}
                                                    {routeInfo.duration && (
                                                        <Chip
                                                            label={routeInfo.duration}
                                                            size="small"
                                                            variant="outlined"
                                                            color="secondary"
                                                        />
                                                    )}
                                                </Box>
                                            )}
                                        </Box>
                                    )}

                                    {/* Pricing Section */}
                                    <Box>
                                        <Typography variant="subtitle2" gutterBottom>
                                            Pricing
                                        </Typography>
                                        {isCalculatingPrice ? (
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <CircularProgress size={16} />
                                                <Typography variant="body2" color="text.secondary">
                                                    Calculating price...
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <Box display="flex" flexDirection="column" gap={0.5}>
                                                <Box display="flex" gap={1} alignItems="center">
                                                    {isEditingPrice ? (
                                                        <>
                                                            <TextField
                                                                size="small"
                                                                type="number"
                                                                value={formData.cost}
                                                                onChange={(e) => {
                                                                    handleInputChange('cost', parseFloat(e.target.value) || 0);
                                                                    setPriceManuallyEdited(true);
                                                                }}
                                                                label="Total"
                                                                InputProps={{
                                                                    startAdornment: <Typography variant="body2" sx={{ mr: 0.5 }}>$</Typography>,
                                                                }}
                                                                sx={{ width: '120px' }}
                                                            />
                                                            <TextField
                                                                size="small"
                                                                type="number"
                                                                value={formData.driversCompensation}
                                                                onChange={(e) => {
                                                                    handleInputChange('driversCompensation', parseFloat(e.target.value) || 0);
                                                                    setPriceManuallyEdited(true);
                                                                }}
                                                                label="Driver"
                                                                InputProps={{
                                                                    startAdornment: <Typography variant="body2" sx={{ mr: 0.5 }}>$</Typography>,
                                                                }}
                                                                sx={{ width: '120px' }}
                                                            />
                                                            <IconButton
                                                                size="small"
                                                                color="primary"
                                                                onClick={() => setIsEditingPrice(false)}
                                                            >
                                                                <CloseIcon fontSize="small" />
                                                            </IconButton>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Chip
                                                                label={`Total: $${formData.cost.toFixed(2)}`}
                                                                color="primary"
                                                                size="medium"
                                                            />
                                                            <Chip
                                                                label={`Driver: $${formData.driversCompensation.toFixed(2)}`}
                                                                color="success"
                                                                variant="outlined"
                                                                size="medium"
                                                            />
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => setIsEditingPrice(true)}
                                                            >
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                        </>
                                                    )}
                                                </Box>
                                                {/* Pricing breakdown */}
                                                {pricingDetails && (
                                                    <Box sx={{ px: 0.5, mt: 0.25 }}>
                                                        <Typography variant="caption" color="text.secondary" display="block">
                                                            {pricingDetails.pricingMethod === 'SET_PRICE'
                                                                ? `Set price · ${pricingDetails.originArea} → ${pricingDetails.destinationArea}`
                                                                : pricingDetails.hourlyRate > 0
                                                                    ? `Formula · hourly (${pricingDetails.tripHours?.toFixed(2)} hr × $${pricingDetails.hourlyRate?.toFixed(2)}/hr)`
                                                                    : pricingDetails.distanceMiles > 0
                                                                        ? `Formula · ${pricingDetails.distanceMiles.toFixed(1)} mi × $${pricingDetails.perMileRate?.toFixed(2)}/mi`
                                                                        : 'Formula pricing'}
                                                        </Typography>
                                                        {(pricingDetails.tollCost > 0 || pricingDetails.rushHourSurcharge > 0 || pricingDetails.stopsCost > 0 || pricingDetails.minimumFareApplied) && (
                                                            <Typography variant="caption" color="text.secondary" display="block">
                                                                {[
                                                                    `base $${(pricingDetails.mileageCost > 0 ? pricingDetails.mileageCost : (pricingDetails.basePrice || 0)).toFixed(2)}`,
                                                                    pricingDetails.tollCost > 0 && `+ tolls $${pricingDetails.tollCost.toFixed(2)}`,
                                                                    pricingDetails.rushHourSurcharge > 0 && `+ rush $${pricingDetails.rushHourSurcharge.toFixed(2)}`,
                                                                    pricingDetails.stopsCost > 0 && `+ stops $${Math.round(pricingDetails.stopsCost)}`,
                                                                    pricingDetails.minimumFareApplied && `(min fare, was $${(pricingDetails.originalPrice || 0).toFixed(2)})`,
                                                                ].filter(Boolean).join(' ')}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                )}
                                            </Box>
                                        )}
                                    </Box>

                                    {/* Notes */}
                                    <TextField
                                        sx={{ flex: 1, minWidth: 200 }}
                                        label="Notes"
                                        multiline
                                        rows={2}
                                        size="small"
                                        value={formData.notes}
                                        onChange={(e) => handleInputChange('notes', e.target.value)}
                                        placeholder="Additional notes..."
                                    />
                                </Box>
                            </CardContent>
                        </Card>

                        {/* Recurring Schedule Section */}
                        <Card sx={{ mt: 2 }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <RepeatIcon /> Recurring Schedule
                                </Typography>

                                {errors.dayConfigs && (
                                    <Typography variant="caption" color="error" sx={{ display: 'block', mb: 2 }}>
                                        {errors.dayConfigs}
                                    </Typography>
                                )}

                                <Grid container spacing={2}>
                                    {dayConfigs.map((dayConfig) => (
                                        <Grid item xs={12} key={dayConfig.dayOfWeek}>
                                            <Paper
                                                elevation={dayConfig.enabled ? 2 : 0}
                                                sx={{
                                                    p: 2,
                                                    border: dayConfig.enabled ? 2 : 1,
                                                    borderColor: dayConfig.enabled ? 'primary.main' : 'divider',
                                                    bgcolor: dayConfig.enabled ? 'action.selected' : 'background.paper'
                                                }}
                                            >
                                                <Grid container spacing={2} alignItems="center">
                                                    <Grid item xs={12} sm={3}>
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={dayConfig.enabled}
                                                                    onChange={() => handleDayToggle(dayConfig.dayOfWeek)}
                                                                    color="primary"
                                                                />
                                                            }
                                                            label={
                                                                <Typography variant="body1" fontWeight={dayConfig.enabled ? 'bold' : 'normal'}>
                                                                    {DAYS_OF_WEEK.find(d => d.value === dayConfig.dayOfWeek)?.label}
                                                                </Typography>
                                                            }
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} sm={4}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            label="Time"
                                                            type="time"
                                                            value={dayConfig.time}
                                                            onChange={(e) => handleDayTimeChange(dayConfig.dayOfWeek, e.target.value)}
                                                            disabled={!dayConfig.enabled}
                                                            error={!!errors[`day_${dayConfig.dayOfWeek}_time`]}
                                                            helperText={errors[`day_${dayConfig.dayOfWeek}_time`]}
                                                            InputLabelProps={{ shrink: true }}
                                                            inputProps={{ step: 300 }}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} sm={5}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            label="End Date"
                                                            type="date"
                                                            value={dayConfig.endDate}
                                                            onChange={(e) => handleDayEndDateChange(dayConfig.dayOfWeek, e.target.value)}
                                                            disabled={!dayConfig.enabled}
                                                            error={!!errors[`day_${dayConfig.dayOfWeek}_endDate`]}
                                                            helperText={errors[`day_${dayConfig.dayOfWeek}_endDate`]}
                                                            InputLabelProps={{ shrink: true }}
                                                            inputProps={{ min: getTodayDate() }}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </Paper>
                                        </Grid>
                                    ))}
                                </Grid>
                            </CardContent>
                        </Card>

                        {/* Right Side - Payment Info */}
                        <Card sx={{ mt: 2 }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Payment Information
                                </Typography>

                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Driver will collect payment at dropoff unless the customer prepays with a credit card below.
                                </Typography>

                                {/* Collapsible Credit Card Section */}
                                <Accordion
                                    expanded={ccAccordionExpanded && !formData.paymentTokenId}
                                    onChange={(e, expanded) => setCcAccordionExpanded(expanded)}
                                    TransitionProps={{ unmountOnExit: true }}
                                    sx={{
                                        mt: 2,
                                        '&:before': { display: 'none' },
                                        boxShadow: 'none',
                                        border: '1px solid',
                                        borderColor: formData.paymentTokenId ? 'success.main' : 'divider',
                                        borderRadius: '8px !important',
                                        bgcolor: formData.paymentTokenId ? 'success.lighter' : 'background.paper'
                                    }}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        sx={{
                                            '& .MuiAccordionSummary-content': {
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                            }
                                        }}
                                    >
                                        <CreditCardIcon color={formData.paymentTokenId ? 'success' : 'action'} />
                                        <Typography>
                                            {formData.paymentTokenId ? 'Credit Card Added ✓' : 'Add Credit Card (Prepay)'}
                                        </Typography>
                                        {formData.paymentTokenId && (
                                            <Chip
                                                label="Prepaid"
                                                size="small"
                                                color="success"
                                                sx={{ ml: 'auto', mr: 1 }}
                                            />
                                        )}
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            Enter credit card details to prepay. Card will be tokenized securely (no raw card data is stored).
                                        </Typography>

                                        <PaymentForm
                                            ref={squarePaymentRef}
                                            onTokenGenerated={handlePaymentTokenGenerated}
                                            onTokenCleared={() => {
                                                setFormData(prev => ({ ...prev, paymentTokenId: null }));
                                                setCcAccordionExpanded(true);
                                            }}
                                            onError={handleTokenError}
                                        />

                                        {errors.paymentTokenId && (
                                            <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                                                {errors.paymentTokenId}
                                            </Typography>
                                        )}
                                    </AccordionDetails>
                                </Accordion>
                            </CardContent>
                        </Card>
                    </Grid>

                </Grid>


            </DialogContent>


            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onCancel} disabled={isSubmitting}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    startIcon={isSubmitting ? <CircularProgress size={20} /> : <SendIcon />}
                    color="primary"
                >
                    {isSubmitting ? 'Creating...' : `Create Recurring Call (${dayConfigs.filter(d => d.enabled).length} ${dayConfigs.filter(d => d.enabled).length === 1 ? 'day' : 'days'})`}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default NewRecurringCallWizard;